import { useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, TrendingDown, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  addDays,
  addMonths,
  eachDay,
  monthOf,
  periodRanges,
  seriesColors,
  todayIn,
  type DashboardPeriod,
} from '@tt/shared';
import { Button } from '@/components/ui/button';
import { Alert, Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui/misc';
import { Segmented } from '@/components/ui/segmented';
import { describeError } from '@/lib/errors';
import {
  formatDate,
  formatDayNumber,
  formatHours,
  formatMonth,
  formatPercent,
  formatWeekday,
  type FormatContext,
} from '@/lib/format';
import { useDashboard, useMe } from '@/lib/queries';
import { useFormat } from '@/lib/use-format';
import { ChartCard, DataTable, type Datum } from './charts';

/** FR5 personal dashboard: period switcher, totals with comparison, breakdowns, top combinations. */
export function DashboardPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const search = useSearch({ from: '/app/dashboard' });
  const today = todayIn(me?.timeZone ?? 'UTC');
  const period: DashboardPeriod = search.period;
  const date = search.date && /^\d{4}-\d{2}-\d{2}$/.test(search.date) ? search.date : today;
  const dashboard = useDashboard(period, date);
  const weekStartDay = me?.organization.weekStartDay ?? 1;

  const setSearch = (next: { period?: DashboardPeriod; date?: string }) =>
    void navigate({ to: '/dashboard', search: { period, date, ...next } });
  const step = (delta: number) => {
    if (period === 'day') setSearch({ date: addDays(date, delta) });
    else if (period === 'week') setSearch({ date: addDays(date, 7 * delta) });
    else setSearch({ date: `${addMonths(monthOf(date), delta)}-01` });
  };

  const d = dashboard.data;
  const range = d?.range ?? periodRanges(period, date, weekStartDay).current;
  const rangeLabel =
    period === 'month'
      ? formatMonth(monthOf(range.from), fmt)
      : period === 'day'
        ? formatDate(range.from, fmt, { dateStyle: 'full' })
        : `${formatDate(range.from, fmt)} – ${formatDate(range.to, fmt)}`;

  const byProject: Datum[] = (d?.byProject ?? []).map((p) => ({
    key: p.projectId,
    label: p.name,
    minutes: p.minutes,
    color: p.color,
  }));
  // Activities carry no colour in the data model, so they take palette slots by id — the same
  // activity keeps its colour when the chart is re-sorted or the period changes.
  const activityColors = seriesColors((d?.byActivity ?? []).map((a) => a.activityId));
  const byActivity: Datum[] = (d?.byActivity ?? []).map((a) => ({
    key: a.activityId,
    label: a.name,
    minutes: a.minutes,
    color: activityColors.get(a.activityId),
  }));
  const dayMap = new Map((d?.byDay ?? []).map((x) => [x.date, x.minutes]));
  const perDay: Datum[] = eachDay(range).map((day) => ({
    key: day,
    label: period === 'month' ? formatDayNumber(day, fmt) : formatWeekday(day, fmt),
    minutes: dayMap.get(day) ?? 0,
  }));
  const names = {
    project: new Map(byProject.map((p) => [p.key, p])),
    activity: new Map(byActivity.map((a) => [a.key, a])),
  };
  const combos: Datum[] = (d?.byCombination ?? []).slice(0, 8).map((c) => ({
    key: `${c.projectId}:${c.activityId}`,
    label: `${names.project.get(c.projectId)?.label ?? '?'} / ${names.activity.get(c.activityId)?.label ?? '?'}`,
    minutes: c.minutes,
    color: names.project.get(c.projectId)?.color,
  }));
  const summarize = (list: Datum[]) =>
    list
      .slice(0, 5)
      .map((x) => `${x.label} ${formatHours(x.minutes, fmt)}`)
      .join(', ');

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <h1 className="text-lg font-semibold">{t('dashboard.title')}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            value={period}
            onChange={(v) => setSearch({ period: v })}
            aria-label={t('dashboard.period')}
            options={[
              { value: 'day', label: t('dashboard.day') },
              { value: 'week', label: t('dashboard.week') },
              { value: 'month', label: t('dashboard.month') },
            ]}
          />
          <div className="inline-flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => step(-1)}
              aria-label={t('app.previous')}
            >
              <ChevronLeft />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSearch({ date: today })}>
              {t('app.today')}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => step(1)}
              aria-label={t('app.next')}
            >
              <ChevronRight />
            </Button>
          </div>
          <span className="text-sm text-muted-foreground" aria-live="polite">
            {rangeLabel}
          </span>
        </div>
      </header>

      {dashboard.isPending ? <Spinner /> : null}
      {dashboard.isError ? (
        <Alert variant="error">{describeError(dashboard.error, t)}</Alert>
      ) : null}

      {d ? (
        <>
          <section
            aria-label={t('dashboard.totalHours')}
            className="grid grid-cols-2 gap-3 lg:grid-cols-4"
          >
            <StatTile
              label={t('dashboard.totalHours')}
              value={formatHours(d.totalMinutes, fmt)}
              delta={
                <Delta
                  current={d.totalMinutes}
                  previous={d.previous.totalMinutes}
                  period={period}
                  fmt={fmt}
                />
              }
            />
            <StatTile label={t('dashboard.entries')} value={String(d.entryCount)} />
            <StatTile label={t('dashboard.activeDays')} value={String(d.activeDays)} />
            <StatTile
              label={t('dashboard.avgPerDay')}
              value={formatHours(d.activeDays ? Math.round(d.totalMinutes / d.activeDays) : 0, fmt)}
            />
          </section>

          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard
              title={t('dashboard.byProject')}
              summary={t('dashboard.chartByProjectAlt', { summary: summarize(byProject) })}
              data={byProject}
              fmt={fmt}
              layout="horizontal"
              total={d.totalMinutes}
              emptyLabel={t('dashboard.noData')}
            />
            <ChartCard
              title={t('dashboard.byActivity')}
              summary={t('dashboard.chartByActivityAlt', { summary: summarize(byActivity) })}
              data={byActivity}
              fmt={fmt}
              layout="horizontal"
              total={d.totalMinutes}
              emptyLabel={t('dashboard.noData')}
            />
          </div>
          <ChartCard
            title={t('dashboard.perDay')}
            summary={t('dashboard.chartPerDayAlt', {
              summary: summarize(perDay.filter((x) => x.minutes > 0)),
            })}
            data={perDay}
            fmt={fmt}
            layout="vertical"
            total={d.totalMinutes}
            emptyLabel={t('dashboard.noData')}
          />

          <Card>
            <CardHeader>
              <CardTitle>{t('dashboard.topCombinations')}</CardTitle>
            </CardHeader>
            <CardContent>
              {combos.length ? (
                <DataTable
                  rows={combos}
                  fmt={fmt}
                  total={d.totalMinutes}
                  labelHeader={`${t('time.project')} / ${t('time.activity')}`}
                />
              ) : (
                <p className="text-sm text-muted-foreground">{t('dashboard.noData')}</p>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function StatTile({
  label,
  value,
  delta,
}: {
  label: string;
  value: string;
  delta?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {delta ? <div className="mt-1 text-xs">{delta}</div> : null}
    </div>
  );
}

function Delta({
  current,
  previous,
  period,
  fmt,
}: {
  current: number;
  previous: number;
  period: DashboardPeriod;
  fmt: FormatContext;
}) {
  const { t } = useTranslation();
  const periodLabel = t(`dashboard.previous${period[0]!.toUpperCase()}${period.slice(1)}`);
  if (previous === 0 && current === 0)
    return <span className="text-muted-foreground">{t('dashboard.noChange')}</span>;
  const diff = current - previous;
  const pct =
    previous > 0 ? formatPercent(Math.abs(diff) / previous, fmt) : formatHours(Math.abs(diff), fmt);
  const Icon = diff >= 0 ? TrendingUp : TrendingDown;
  const text =
    diff === 0
      ? t('dashboard.noChange')
      : diff > 0
        ? t('dashboard.increase', { value: pct })
        : t('dashboard.decrease', { value: pct });
  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground">
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {text} {t('dashboard.vsPrevious', { period: periodLabel })}
    </span>
  );
}
